import { useState } from 'react';
import { Input, Checkbox, Button } from 'antd';
import { SearchOutlined, StopOutlined } from '@ant-design/icons';
import { useGetLeadGroupsQuery } from '../../services/leadGroups';
import './styles.scss';

interface Group {
  id:    string;
  label: string;
  color: string;
}

interface Props {
  open:      boolean;
  onClose:   () => void;
  onSelect?: (ids: string[]) => void;
  selected?: string[];
}

const NONE_ID = '__none__';

const GroupSelect = ({ open, onClose, onSelect, selected = [] }: Props) => {
  const [search, setSearch]     = useState('');
  const [picked, setPicked]     = useState<string[]>(selected);

  // Groups from the API (GET /lead-groups).
  const { data: groupsResp, isFetching } = useGetLeadGroupsQuery(undefined, { skip: !open });
  const GROUPS: Group[] = ((groupsResp?.data ?? groupsResp ?? []) as any[]).map((g, i) => ({
    id:    g._id ?? g.id ?? `group-${i}`,
    label: g.name ?? '—',
    color: g.colour ?? g.color ?? '#64748b',
  }));

  const filtered = GROUPS.filter(g => g.label.toLowerCase().includes(search.toLowerCase()));
  const showNone = 'not in any groups'.includes(search.toLowerCase()) || !search.trim();

  const toggle = (id: string) =>
    setPicked(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const handleDone = () => {
    onSelect?.(picked);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="group-select">
      <div className="gs-search">
        <Input
          placeholder="Search Groups"
          prefix={<SearchOutlined style={{ color: '#999' }} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="gs-list">
        {showNone && (
          <div className="gs-item" onClick={() => toggle(NONE_ID)}>
            <span className="gs-none-icon"><StopOutlined /></span>
            <span className="gs-label">Not in any groups</span>
            <Checkbox
              checked={picked.includes(NONE_ID)}
              onClick={e => e.stopPropagation()}
              onChange={() => toggle(NONE_ID)}
            />
          </div>
        )}

        {filtered.map(g => (
          <div key={g.id} className="gs-item" onClick={() => toggle(g.id)}>
            <span className="gs-dot" style={{ background: g.color }} />
            <span className="gs-label">{g.label}</span>
            <Checkbox
              checked={picked.includes(g.id)}
              onClick={e => e.stopPropagation()}
              onChange={() => toggle(g.id)}
            />
          </div>
        ))}

        {isFetching && <div className="gs-empty">Loading…</div>}
        {!isFetching && !showNone && filtered.length === 0 && <div className="gs-empty">No groups found</div>}
      </div>

      <div className="gs-done">
        <Button type="primary" block size="large" onClick={handleDone}>
          ✓ DONE
        </Button>
      </div>
    </div>
  );
};

export default GroupSelect;
